import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PageBlocks } from "@/components/content/PageBlocks";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { crusadePages } from "@/data/crusades";
import { pageBlocks } from "@/lib/content";

const crusades = crusadePages;

export const Route = createFileRoute("/crusade/$slug")({
  loader: ({ params }) => {
    const crusade = crusades.find((c) => c.slug === params.slug);
    if (!crusade) throw notFound();
    return { crusade };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Crusade — Light of Life International" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { city, country, dates } = loaderData.crusade;
    const title = `${city}, ${country} Crusade — Light of Life International`;
    const description = `Join the Light of Life International gospel crusade in ${city}, ${country}, ${dates}.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://loli.ardent.dev/crusade/${params.slug}` },
      ],
      links: [{ rel: "canonical", href: `https://loli.ardent.dev/crusade/${params.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: `${city}, ${country} Crusade`,
            description,
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            location: {
              "@type": "Place",
              name: `${city}, ${country}`,
              address: { "@type": "PostalAddress", addressLocality: city, addressCountry: country },
            },
            image: `https://loli.ardent.dev${loaderData.crusade.image}`,
            url: `https://loli.ardent.dev/crusade/${params.slug}`,
            organizer: {
              "@type": "Organization",
              name: "Light of Life International",
              url: "https://loli.ardent.dev/",
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: CrusadeNotFound,
  component: CrusadePage,
});

function CrusadeNotFound() {
  return (
    <div className="min-h-dvh bg-background">
      <Header overlay={false} />
      <main id="main" className="container-site max-w-3xl section-y">
        <h1 className="text-4xl text-navy">Crusade not found</h1>
        <Link to="/crusades" className="mt-4 inline-block font-heading text-sm uppercase underline">
          See all crusades
        </Link>
      </main>
      <Footer />
    </div>
  );
}

function CrusadePage() {
  const { crusade } = Route.useLoaderData();
  const blocks = pageBlocks(crusade.contentKey).filter((b) => b.t !== "h1");

  return (
    <div className="min-h-dvh bg-background">
      <Header />
      <section className="relative flex min-h-[480px] items-end">
        <img src={crusade.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" aria-hidden />
        <div className="container-site relative pb-16 pt-32">
          <h1 className="text-5xl text-white sm:text-6xl">
            {crusade.city}
            <span className="block font-bold">{crusade.country}</span>
          </h1>
          <p className="eyebrow mt-4 text-xl text-white">{crusade.dates}</p>
          <Link
            to="/join-us"
            className="mt-8 inline-block rounded border-2 border-white px-8 py-3 font-heading text-sm uppercase tracking-widest text-white transition-colors hover:border-brand hover:bg-brand hover:text-brand-foreground"
          >
            Sign Up
          </Link>
        </div>
      </section>
      <main id="main" className="container-site max-w-3xl section-y">
        <PageBlocks blocks={blocks} />
      </main>
      <Footer />
    </div>
  );
}
